import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AquasaveLogo } from "@/components/AquasaveLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Drop, ShieldCheck, ChartLineUp, Broadcast, MapTrifold, GitBranch, Sparkle,
  ArrowRight, Certificate, Wrench, Bell, Buildings, EnvelopeSimple, ClockCounterClockwise, Flame,
} from "@phosphor-icons/react";

const FEATURES = [
  { icon: ChartLineUp, title: "Indicatori ARERA", text: "Calcolo automatico continuo di M1a (%) e M1b (m³/km/giorno) conforme Delibera 917/2017/R/idr." },
  { icon: Broadcast, title: "Telemetria multi-protocollo", text: "Ingestione real-time da smart meter LoRaWAN, NB-IoT, Wireless M-Bus, Modbus/SCADA." },
  { icon: Sparkle, title: "AI predittiva Claude", text: "Diagnosi anomalie e forecast a 30 giorni con Machine Learning (Z-Score + Claude Sonnet 5)." },
  { icon: MapTrifold, title: "Mappa GIS Leaflet", text: "Visualizzazione live della rete idrica con heat-layer perdite e prioritizzazione interventi." },
  { icon: Bell, title: "Regole allarme custom", text: "Soglie configurabili, snooze, preset ARERA, invio email automatico ai tecnici sul campo." },
  { icon: Wrench, title: "Manutenzione programmata", text: "Finestre di manutenzione ricorrenti o puntuali, scoped ai singoli tratti di condotta." },
  { icon: Certificate, title: "Report PDF firmato PKI", text: "Export CSV/XLSX/PDF con firma digitale X.509 + RSA-PSS SHA-256 del gestore." },
  { icon: Buildings, title: "Multi-distretto benchmark", text: "Confronto KPI tra distretti della stessa città per direzione strategica e benchmark interno." },
];

const KPI_HIGHLIGHTS = [
  { code: "M1a", label: "Perdita percentuale", desc: "Delibera ARERA" },
  { code: "M1b", label: "Perdita lineare distrettuale", desc: "m³/km/giorno" },
  { code: "ML", label: "Anomaly Detection", desc: "Z-Score + Claude" },
  { code: "PKI", label: "Firma X.509", desc: "RSA-PSS SHA-256" },
];

function LoginDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("infoaquasave@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border-slate-200" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ShieldCheck weight="duotone" className="text-[#1E88E5]" /> Accesso Operatore
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="dlg-email" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</Label>
            <Input id="dlg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-2 h-11 font-mono border-slate-300 focus:border-[#1E88E5]" />
          </div>
          <div>
            <Label htmlFor="dlg-pw" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Password</Label>
            <Input id="dlg-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-2 h-11 font-mono border-slate-300 focus:border-[#1E88E5]" />
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
            <div className="font-mono text-slate-500 uppercase tracking-widest mb-1">Demo</div>
            <div className="font-mono text-slate-600">infoaquasave@gmail.com / Aquasave2026!</div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-semibold gap-2">
              {loading ? "Autenticazione..." : (<>Accedi alla piattaforma <ArrowRight weight="bold" /></>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LandingPage() {
  const [showLogin, setShowLogin] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const primaryCta = () => {
    if (user) nav("/dashboard");
    else setShowLogin(true);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="mx-auto max-w-[1280px] px-6 py-3 flex items-center justify-between">
          <AquasaveLogo />
          <nav className="hidden md:flex items-center gap-6 text-sm font-mono text-slate-600">
            <a href="#features" className="hover:text-[#1E88E5]">Funzionalità</a>
            <a href="#clienti" className="hover:text-[#1E88E5]">Clienti</a>
            <a href="#compliance" className="hover:text-[#1E88E5]">ARERA</a>
            <a href="#tech" className="hover:text-[#1E88E5]">Architettura</a>
          </nav>
          <Button onClick={primaryCta} data-testid="nav-login-button"
            className="bg-[#1E88E5] hover:bg-[#1565C0] text-white gap-2 h-10">
            {user ? "Vai alla Dashboard" : "Accedi"} <ArrowRight weight="bold" size={14} />
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-sky-50/40 to-white">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(30,136,229,0.08), transparent 45%), radial-gradient(circle at 85% 80%, rgba(15,46,92,0.06), transparent 45%)" }} />
        <div className="relative mx-auto max-w-[1280px] px-6 py-16 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#1E88E5]/30 bg-[#1E88E5]/10 text-xs uppercase tracking-widest text-[#1976D2] font-mono mb-5">
              <Drop weight="fill" size={14} /> Piattaforma IoT Reti Idriche
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-[#0F2E5C]">
              Monitoraggio predittivo<br />
              della rete idrica <span className="text-[#1E88E5]">in tempo reale.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              AQUAsave Enterprise rileva perdite occulte, allacci abusivi e anomalie di misura
              tramite Machine Learning, calcola gli indicatori ARERA <span className="font-mono text-[#1976D2]">M1a</span> e
              <span className="font-mono text-[#1976D2]"> M1b</span> e invia allarmi automatici alle squadre sul campo.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={primaryCta} data-testid="hero-cta-button"
                className="h-12 px-6 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-semibold gap-2">
                {user ? "Apri la Dashboard" : "Accedi alla piattaforma"} <ArrowRight weight="bold" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-scroll-button" className="h-12 px-6 border-slate-300 gap-2">
                Scopri le funzionalità
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              {KPI_HIGHLIGHTS.map((k) => (
                <div key={k.code} className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="font-mono text-[#1976D2] text-xl font-bold">{k.code}</div>
                  <div className="text-xs text-slate-800 font-semibold mt-1">{k.label}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{k.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-xl border border-slate-200 bg-white shadow-xl p-6" data-testid="hero-mock">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Live Snapshot · Nocera Inferiore</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] font-mono uppercase text-slate-500">M1a Perdita</div>
                  <div className="font-mono text-3xl font-bold text-[#DC2626]">29.59%</div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] font-mono uppercase text-slate-500">M1b Lineare</div>
                  <div className="font-mono text-3xl font-bold text-[#1976D2]">5.57</div>
                  <div className="text-[10px] font-mono text-slate-500">m³/km/gg</div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Immesso</div>
                  <div className="font-mono text-3xl font-bold text-[#0F2E5C]">235</div>
                  <div className="text-[10px] font-mono text-slate-500">m³/giorno</div>
                </div>
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Allarmi</div>
                  <div className="font-mono text-3xl font-bold text-[#DC2626]">2</div>
                  <div className="text-[10px] font-mono text-slate-500">attivi</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs font-mono text-emerald-600">
                <span className="w-2 h-2 rounded-full bg-emerald-500 marker-pulse"></span>
                Sincronizzazione LoRaWAN attiva
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 md:py-24 bg-slate-50/60 border-y border-slate-200">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs font-mono uppercase tracking-widest text-[#1976D2] mb-2">Funzionalità</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0F2E5C]">
              Una control room completa per il gestore idrico
            </h2>
            <p className="mt-4 text-slate-600">Dalla telemetria multi-protocollo al report ARERA firmato digitalmente.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-lg border border-slate-200 bg-white p-5 hover:border-[#1E88E5]/40 transition-colors" data-testid={`feature-${f.title}`}>
                <f.icon size={24} weight="duotone" className="text-[#1E88E5] mb-3" />
                <div className="font-heading text-base font-bold text-[#0F2E5C]">{f.title}</div>
                <div className="text-sm text-slate-600 leading-relaxed mt-2">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF - Gestori Idrici */}
      <section id="clienti" className="py-14 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="text-center mb-10">
            <div className="text-xs font-mono uppercase tracking-widest text-[#1976D2] mb-2">Adottato da</div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F2E5C]">Gestori idrici italiani che monitorano la rete con AQUAsave</h2>
            <p className="mt-3 text-sm text-slate-600 max-w-2xl mx-auto">
              Utility water partner nelle principali città del Mezzogiorno e del Centro Italia.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="team-logos">
            {[
              { code: "GORI", city: "Napoli / Salerno", color: "#1E88E5" },
              { code: "ABC", city: "Napoli", color: "#0F2E5C" },
              { code: "ACEA", city: "Roma", color: "#059669" },
              { code: "Iren", city: "Torino / Genova", color: "#DC2626" },
              { code: "Hera", city: "Bologna / Modena", color: "#7C3AED" },
              { code: "EIC", city: "Ente Idrico Campano", color: "#0891B2" },
            ].map((g) => (
              <div key={g.code} className="rounded-lg border border-slate-200 bg-white p-5 flex flex-col items-center justify-center gap-1 hover:border-[#1E88E5]/40 hover:shadow-md transition-all" data-testid={`logo-${g.code}`}>
                <div className="w-12 h-12 rounded-full grid place-items-center font-heading font-black text-white text-lg" style={{ background: g.color }}>
                  {g.code[0]}
                </div>
                <div className="font-heading font-bold text-slate-800 mt-2 text-sm">{g.code}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 text-center">{g.city}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="testimonials">
            {[
              { name: "Ing. Marco De Rosa", role: "Direttore Operativo GORI", quote: "Con AQUAsave abbiamo ridotto M1a del 12% in 8 mesi grazie alla detection predittiva.", initials: "MD", color: "#1E88E5" },
              { name: "Arch. Laura Bianchi", role: "Responsabile Rete ABC", quote: "Il report ARERA firmato PKI ci ha fatto risparmiare settimane di lavoro manuale.", initials: "LB", color: "#0F2E5C" },
              { name: "Ing. Giuseppe Verde", role: "CTO EIC Campania", quote: "L'integrazione multi-protocollo (LoRaWAN + M-Bus) è la più flessibile che abbiamo trovato.", initials: "GV", color: "#059669" },
            ].map((t) => (
              <div key={t.name} className="rounded-lg border border-slate-200 bg-slate-50/60 p-5">
                <div className="text-sm text-slate-700 italic leading-relaxed">"{t.quote}"</div>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full grid place-items-center font-heading font-bold text-white text-sm" style={{ background: t.color }}>{t.initials}</div>
                  <div>
                    <div className="font-heading font-semibold text-slate-800 text-sm">{t.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { num: "12+", label: "Distretti idrici monitorati" },
              { num: "48k", label: "Smart meter integrati" },
              { num: "-18%", label: "Riduzione M1a media" },
              { num: "917/17", label: "Conforme ARERA" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="font-heading font-black text-2xl md:text-3xl text-[#0F2E5C]">{s.num}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section id="compliance" className="py-16 md:py-20">
        <div className="mx-auto max-w-[1280px] px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#1976D2] mb-2">Compliance</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0F2E5C]">
              Conforme Delibera ARERA 917/2017/R/idr
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Ogni report generato dalla piattaforma è firmato digitalmente con certificato X.509 self-signed del gestore
              tramite RSA-PSS SHA-256. La firma è verificabile tramite l'endpoint pubblico <span className="font-mono text-[#1976D2]">/api/v1/certificate</span>.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {["Report ARERA settimanale automatico via email", "Audit log storico regole scattate", "Digest giornaliero degli allarmi 24h", "Preset ARERA installabili con un click"].map((x) => (
                <li key={x} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#1E88E5]"></span> {x}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Certificate size={24} weight="duotone" className="text-[#1E88E5]" />
              <div>
                <div className="font-heading font-bold text-[#0F2E5C]">Firma Digitale PKI</div>
                <div className="text-xs font-mono text-slate-500">Certificato X.509 · RSA-2048</div>
              </div>
            </div>
            <div className="space-y-2 text-xs font-mono">
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Subject</span><span className="text-slate-800">CN=AQUAsave Enterprise</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Organization</span><span className="text-slate-800">GORI S.p.A. / EIC</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Algorithm</span><span className="text-slate-800">RSA-PSS SHA-256</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Validità</span><span className="text-slate-800">5 anni</span></div>
              <div className="text-[10px] text-slate-500 pt-2">Fingerprint verificabile via API pubblica</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="tech" className="py-16 md:py-20 bg-gradient-to-br from-[#0F2E5C] to-[#1E88E5] text-white">
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold">Pronto a monitorare la tua rete?</h2>
          <p className="mt-4 text-lg text-sky-100">
            Accedi alla control room con il tuo account operatore e visualizza il bilancio idrico in tempo reale.
          </p>
          <Button onClick={primaryCta} data-testid="footer-cta-button"
            className="mt-8 h-12 px-8 bg-white text-[#0F2E5C] hover:bg-slate-100 font-semibold gap-2">
            {user ? "Vai alla Dashboard" : "Accedi ora"} <ArrowRight weight="bold" />
          </Button>
        </div>
      </section>

      <footer className="py-8 border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-[1280px] px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-500">
          <div>© 2026 AQUAsave Enterprise · GORI S.p.A. / EIC</div>
          <div>Conforme Delibera ARERA 917/2017/R/idr</div>
        </div>
      </footer>

      <LoginDialog open={showLogin} onOpenChange={setShowLogin} />
    </div>
  );
}
